import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { LOGO_NAME_GREEN, API } from "../constants";

/**
 * RecipePage — admin-gated brand-recipe filler.
 * Paste or upload a BRAND_VALUES.<brand>.json, get back the filled
 * RECREATE_WEBSITE_PROMPT.md ready to paste into a fresh build session.
 */
export default function RecipePage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [jsonText, setJsonText] = useState("");
  const [brandHint, setBrandHint] = useState("");
  const [output, setOutput] = useState(null);
  const [busy, setBusy] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/verify-password`, { password });
      setIsAuthenticated(true);
      // Auto-fetch blank template as a starting point
      try {
        const r = await axios.get(`${API}/admin/recipe/template`, { params: { password } });
        setJsonText(JSON.stringify(r.data, null, 2));
      } catch { /* not fatal — user can paste their own */ }
    } catch {
      toast.error("Invalid password");
    }
  };

  const loadFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setJsonText(String(ev.target.result || ""));
      // Derive a hint from the filename: BRAND_VALUES.acme.json → acme
      const m = f.name.match(/BRAND_VALUES\.([\w-]+)\.json$/i);
      if (m) setBrandHint(m[1]);
    };
    reader.readAsText(f);
  };

  const generate = async () => {
    setBusy(true);
    setOutput(null);
    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (e) {
      toast.error("Invalid JSON — fix the syntax error first");
      setBusy(false);
      return;
    }
    try {
      const r = await axios.post(`${API}/admin/recipe/fill`, {
        password,
        values: parsed,
        filename_hint: brandHint || null,
      });
      setOutput(r.data);
      if (r.data.missing_placeholders?.length) {
        toast.warning(`${r.data.missing_placeholders.length} placeholder(s) unresolved — see panel`);
      } else {
        toast.success(`Resolved all ${r.data.resolved_count} placeholders`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fill failed");
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (!output) return;
    const blob = new Blob([output.filled_markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = output.filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output.filled_markdown);
      toast.success("Filled prompt copied to clipboard");
    } catch {
      toast.error("Clipboard access blocked");
    }
  };

  if (!isAuthenticated) {
    return (
      <Layout>
        <div className="min-h-[calc(100vh-8rem)] flex items-center justify-center px-4">
          <div className="card-glass p-8 w-full max-w-xs" data-testid="recipe-login">
            <div className="text-center mb-8">
              <img src={LOGO_NAME_GREEN} alt="Top Screen" className="mx-auto mb-4" style={{ height: "180px" }} />
              <h1 className="font-heading text-2xl font-bold text-white">Brand Recipe</h1>
              <p className="text-[#E8E4DC]">Enter Password to Continue</p>
            </div>
            <form onSubmit={handleLogin}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-dark w-full mb-4 text-center"
                placeholder="Password"
                data-testid="recipe-password"
              />
              <button type="submit" className="btn-primary w-full" data-testid="recipe-submit">
                Access Recipe Filler
              </button>
            </form>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-[calc(100vh-8rem)] py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-heading text-4xl font-bold text-white mb-2" data-testid="recipe-title">
            Brand Recipe Filler
          </h1>
          <p className="text-[#E8E4DC] mb-8">
            Upload or paste a <code className="text-[#A3E635]">BRAND_VALUES.&lt;brand&gt;.json</code>.
            The server runs <code className="text-[#A3E635]">fill_prompt.py</code> and hands back the
            ready-to-paste master prompt. Same engine as the CLI — same guarantees.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input pane */}
            <div className="card-glass p-6" data-testid="recipe-input-pane">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-semibold text-white">Brand Values JSON</h2>
                <label className="text-xs text-[#A3E635] cursor-pointer hover:underline">
                  Upload .json
                  <input type="file" accept=".json,application/json" className="hidden" onChange={loadFile} data-testid="recipe-file-input" />
                </label>
              </div>

              <div className="mb-3">
                <label className="block text-[#E8E4DC] text-xs mb-1">Brand slug (used in download filename)</label>
                <input
                  type="text"
                  value={brandHint}
                  onChange={(e) => setBrandHint(e.target.value)}
                  className="input-dark w-full"
                  placeholder="acme"
                  data-testid="recipe-brand-hint"
                />
              </div>

              <textarea
                value={jsonText}
                onChange={(e) => setJsonText(e.target.value)}
                className="input-dark w-full font-mono text-xs"
                style={{ height: "420px", whiteSpace: "pre" }}
                placeholder='Paste BRAND_VALUES.<brand>.json here…'
                data-testid="recipe-json-textarea"
              />

              <button
                onClick={generate}
                disabled={busy || !jsonText.trim()}
                className="btn-primary w-full mt-4 disabled:opacity-50"
                data-testid="recipe-generate"
              >
                {busy ? "Filling…" : "Generate filled prompt →"}
              </button>
            </div>

            {/* Output pane */}
            <div className="card-glass p-6" data-testid="recipe-output-pane">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-xl font-semibold text-white">Filled Prompt</h2>
                {output && (
                  <div className="flex gap-2">
                    <button onClick={copyToClipboard} className="btn-secondary text-xs px-3 py-1" data-testid="recipe-copy">
                      Copy
                    </button>
                    <button onClick={download} className="btn-primary text-xs px-3 py-1" data-testid="recipe-download">
                      Download {output.filename}
                    </button>
                  </div>
                )}
              </div>

              {!output && (
                <p className="text-[#E8E4DC]/60 text-sm py-8 text-center">
                  No output yet. Fill in the JSON on the left and hit <span className="text-[#A3E635]">Generate</span>.
                </p>
              )}

              {output && (
                <>
                  <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
                    <div className="bg-white/5 rounded p-2 text-center">
                      <div className="text-[#A3E635] font-bold text-base" data-testid="recipe-stat-resolved">{output.resolved_count}</div>
                      <div className="text-[#E8E4DC]/60">resolved</div>
                    </div>
                    <div className={`bg-white/5 rounded p-2 text-center ${output.missing_placeholders.length ? "ring-1 ring-red-500/50" : ""}`}>
                      <div className={`font-bold text-base ${output.missing_placeholders.length ? "text-red-400" : "text-[#A3E635]"}`} data-testid="recipe-stat-missing">{output.missing_placeholders.length}</div>
                      <div className="text-[#E8E4DC]/60">missing</div>
                    </div>
                    <div className="bg-white/5 rounded p-2 text-center">
                      <div className="text-[#A3E635] font-bold text-base">{(output.size_bytes / 1024).toFixed(1)} KB</div>
                      <div className="text-[#E8E4DC]/60">filled size</div>
                    </div>
                  </div>

                  {output.missing_placeholders.length > 0 && (
                    <div className="mb-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-xs">
                      <p className="text-red-300 font-semibold mb-1">Unresolved placeholders:</p>
                      <p className="text-[#E8E4DC] font-mono">{output.missing_placeholders.map(n => `#${n}`).join(", ")}</p>
                    </div>
                  )}

                  <pre className="bg-black/40 rounded-lg p-3 text-[#E8E4DC] text-[10px] overflow-auto font-mono"
                       style={{ height: "400px", whiteSpace: "pre" }}
                       data-testid="recipe-output-preview">
                    {output.filled_markdown.slice(0, 6000)}
                    {output.filled_markdown.length > 6000 ? `\n\n… (${output.filled_markdown.length - 6000} more chars — download for the full file)` : ""}
                  </pre>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
